/* eslint-disable @typescript-eslint/naming-convention */
import type React from 'react';
import { useEffect } from 'react';

import { isEmbeddableExplorerRoute } from 'src/app/embeddableExplorer/isEmbeddableExplorerRoute';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { login as loginRoute } from 'src/app/onboarding/routes/routes';
import { useIdentity } from 'src/hooks/useIdentity';
import { useIsCheckedRoute } from 'src/hooks/useIsCheckedRoute';
import { shouldOptOutOfAmplitude } from 'src/lib/analytics/amplitude/helpers/shouldOptOutOfAmplitude';
import { isCypressUserEmail } from 'src/lib/isCypressUser';
import { useLDFlag } from 'src/lib/launchDarkly';
import { amplitudeClient } from 'src/lib/tracking';

type amplitudeFlagConfig = {
  trackingEnabled: boolean;
  trackEmbeddedUsers: boolean;
};

type ReactChildren = {
  children: React.ReactElement;
};

const useAmplitudeConfig = (): amplitudeFlagConfig => {
  const trackingEnabled = useLDFlag('studio-amplitude-tracking-enabled');

  const trackEmbeddedUsers = useLDFlag('betelgeuse-amplitude-in-embedded');

  return {
    trackingEnabled,
    trackEmbeddedUsers,
  };
};

export const AmplitudeProvider = ({ children }: ReactChildren) => {
  const { me, isUser, myAccount, memberships } = useIdentity();
  const graphRef = useGraphRef();

  const amplitudeConfiguration = useAmplitudeConfig();
  const excludeRouteFromTracking =
    useIsCheckedRoute(loginRoute.definition) || isEmbeddableExplorerRoute();

  useEffect(() => {
    const shouldOptOut = shouldOptOutOfAmplitude({
      excludeRouteFromTracking,
      amplitudeConfiguration,
    });
    amplitudeClient.setOptOut(shouldOptOut);
  }, [excludeRouteFromTracking, amplitudeConfiguration]);

  useEffect(() => {
    // Identify when `me` or `isUser` is updated
    if (me && isUser) {
      // Find the user's role for the current active account
      const userRole = memberships.find(
        (membership) => membership.account.id === myAccount?.id,
      )?.role;

      void amplitudeClient.identify(
        {
          'Active Org Id': myAccount?.internalID,
          active_org_subscription_tier: myAccount?.currentPlan?.tier,
          'Email Verified': me.emailVerified,
          Email: me?.email || undefined,
          'Full Name': me.fullName,
          user_role: userRole,
        },
        me.id,
      );
    }
  }, [
    me,
    isUser,
    graphRef,
    myAccount?.internalID,
    myAccount?.id,
    myAccount?.currentPlan?.tier,
    memberships,
  ]);

  // Remove Amplitude Session Replay plugin for Cypress test users after authentication
  useEffect(() => {
    if (me && isUser) {
      const userEmail = me.email;
      if (userEmail && isCypressUserEmail(userEmail)) {
        amplitudeClient.remove('@amplitude/plugin-session-replay-browser');
      }
    }
  }, [me, isUser]);

  return children;
};
